// frontend/src/pages/iqgames/fanratings/FanRatingsRatingPage.jsx

import React, { useEffect, useState } from "react";
import useFanRatingsHook from "./useFanRatingsHook";
import { useSelector } from "react-redux";

const FanRatingsRatingPage = ({ question, onBack, onSubmit }) => {
  const { getQuestionById, submitRating } = useFanRatingsHook();
  const authState = useSelector((state) => state.authReducer);

  const userId = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode;
  const userName = authState?.user?.name;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [ratings, setRatings] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuestion();
  }, [question]);

  const loadQuestion = async () => {
    try {
      setLoading(true);

      if (!question?._id) return;
      const response = await getQuestionById(question._id);

      if (response?.success) {
        setItems(response.data.items || []);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error("FanRatings question load error:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRating = (name, value) => {
    setRatings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const ratedCount = Object.keys(ratings).length;

  const averageRating = ratedCount
    ? (
        Object.values(ratings).reduce((a, b) => a + b, 0) /
        ratedCount
      ).toFixed(1)
    : "0.0";

  const handleSubmit = async () => {
    if (ratedCount !== items.length) {
      alert("Please rate all items.");
      return;
    }

    const userResponse = items
      .map((item) => ratings[item.name] || 0)
      .join(",");

    const payload = {
      questionId: question._id,
      userId,
      userCode,
      userName,
      userResponse,
    };

    try {
      setSubmitting(true);

      const response = await submitRating(payload);

      if (response?.success) {
        if (onSubmit) {
          onSubmit({
            ...payload,
            result: response.data,
          });
        }
      }
    } catch (error) {
      console.error("FanRatings submit error:", error);
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-gray-50">
        Loading Rating Question...
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="mb-3 rounded-lg bg-fuchsia-700 w-fit px-3 py-2 text-xs text-white"
      >
        ← Back
      </button>

      <div className="rounded-xl bg-purple-900 border border-fuchsia-700 p-4">
        <h2 className="text-lg font-bold text-gray-50">
          ⭐ {question.title}
        </h2>

        <p className="mt-1 text-xs text-gray-100">
          {question.description}
        </p>

        <div className="mt-4 flex justify-between text-xs">
          <span className="text-gray-100">
            Rated {ratedCount}/{items.length}
          </span>

          <span className="text-yellow-400 font-bold">
            Avg {averageRating}/{question.ratingScale}
          </span>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {items.map((item) => (
          <div
            key={item.name}
            className="rounded-xl bg-purple-900 border border-fuchsia-700 p-4"
          >
            <h3 className="text-sm font-semibold text-gray-50">
              {item.name}
            </h3>

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {Array.from(
                { length: question.ratingScale },
                (_, i) => i + 1,
              ).map((value) => (
                <button
                  key={value}
                  onClick={() => handleRating(item.name, value)}
                  className={`h-9 w-9 rounded-full text-xs font-bold transition ${
                    ratings[item.name] >= value
                      ? "bg-yellow-400 text-black"
                      : "bg-purple-800 text-gray-400"
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>

            {ratings[item.name] && (
              <div className="mt-3 text-center">
                <span className="rounded-full bg-fuchsia-700 px-3 py-1 text-xs text-white">
                  Your Rating: {ratings[item.name]}/{question.ratingScale}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl bg-purple-900 border border-fuchsia-700 p-4">
        <h3 className="text-sm font-semibold text-gray-50">
          Rating Summary
        </h3>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-lg bg-purple-950 p-3">
            <p className="text-gray-100">Completed</p>
            <p className="mt-1 text-xl font-bold text-white">
              {ratedCount}/{items.length}
            </p>
          </div>

          <div className="rounded-lg bg-purple-950 p-3">
            <p className="text-gray-100">Average</p>
            <p className="mt-1 text-xl font-bold text-yellow-400">
              {averageRating}/{question.ratingScale}
            </p>
          </div>
        </div>
      </div>

      <button
        disabled={submitting || ratedCount !== items.length}
        onClick={handleSubmit}
        className={`mt-6 w-full rounded-xl py-3 font-semibold ${
          ratedCount === items.length
            ? "bg-fuchsia-600 text-white"
            : "bg-gray-700 text-gray-400"
        }`}
      >
        {submitting ? "Submitting..." : "Submit Rating"}
      </button>
    </div>
  );
};

export default FanRatingsRatingPage;